import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, KeyRound, ScanLine, Copy } from "lucide-react";

export default function Admin2FA() {
  const [status, setStatus] = useState({ enabled: false, setup_pending: false });
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try { setStatus((await api.get("/admin/2fa/status")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    try {
      const r = await api.post("/admin/2fa/setup");
      setSetup(r.data);
      setCode("");
      toast.success("Scannez le QR avec Google Authenticator");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const verify = async () => {
    if (!code) return toast.error("Saisissez le code à 6 chiffres");
    setSubmitting(true);
    try {
      await api.post("/admin/2fa/verify", { code });
      toast.success("2FA activée ! 🔐 Conservez votre secret en lieu sûr.");
      setSetup(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const disable = async () => {
    if (!code) return toast.error("Saisissez un code valide pour confirmer");
    setSubmitting(true);
    try {
      await api.post("/admin/2fa/disable", { code });
      toast.success("2FA désactivée");
      setCode("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-full" data-testid="admin-2fa-page">
      <PageHeader
        eyebrow="Admin · Sécurité"
        title="Authentification forte (2FA)"
        sub="Protégez votre compte admin avec Google Authenticator, Authy ou 1Password. À l'usage : code à 6 chiffres demandé à chaque login."
      />

      <div className="p-6 lg:p-10 grid lg:grid-cols-2 gap-6">
        <section className="bg-white border border-[#DEE2E6] p-6" data-testid="2fa-status">
          <div className="flex items-center gap-3">
            {status.enabled ? <ShieldCheck className="h-8 w-8 text-[#059669]" /> : <ShieldOff className="h-8 w-8 text-[#868E96]" />}
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: status.enabled ? "#059669" : "#868E96" }}>
                {status.enabled ? "Active" : "Inactive"}
              </div>
              <div className="font-display text-xl font-black tracking-tight">
                {status.enabled ? "2FA TOTP activée" : "Aucune 2FA configurée"}
              </div>
            </div>
          </div>

          {!status.enabled && !setup && (
            <Button onClick={start} data-testid="2fa-start-btn" className="mt-6 h-11 bg-[#002FA7] text-white"><KeyRound className="h-4 w-4 mr-2" /> Activer la 2FA</Button>
          )}

          {status.enabled && (
            <div className="mt-6 space-y-3">
              <div className="text-xs text-[#495057]">Pour désactiver, saisissez un code à 6 chiffres généré par votre application :</div>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" maxLength={6} className="h-11 font-mono text-xl tracking-widest text-center" data-testid="2fa-disable-code" />
              <Button onClick={disable} disabled={submitting} variant="outline" data-testid="2fa-disable-btn" className="h-11 border-[#E60000] text-[#E60000]"><ShieldOff className="h-4 w-4 mr-2" /> Désactiver la 2FA</Button>
            </div>
          )}
        </section>

        {setup && (
          <section className="bg-white border border-[#DEE2E6] p-6" data-testid="2fa-setup">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#002FA7]">Étape 1 — Scannez le QR</div>
            <div className="mt-3 flex flex-col items-center">
              <img src={setup.qr_png_data_url} alt="QR Code" className="w-48 h-48 border border-[#DEE2E6]" data-testid="2fa-qr" />
              <div className="mt-4 text-[10px] text-[#868E96] text-center max-w-xs">Ou saisissez manuellement la clé dans votre app :</div>
              <div className="mt-2 flex gap-2 w-full">
                <Input readOnly value={setup.secret} className="h-9 font-mono text-xs" data-testid="2fa-secret" />
                <Button size="sm" variant="outline" data-testid="2fa-copy" onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Secret copié"); }} className="h-9 px-3"><Copy className="h-3 w-3" /></Button>
              </div>
            </div>

            <div className="mt-6 text-[10px] uppercase tracking-[0.3em] font-bold text-[#002FA7]">Étape 2 — Validation</div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold mt-3 block">Code à 6 chiffres</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" maxLength={6} className="h-12 mt-1 font-mono text-2xl tracking-widest text-center" data-testid="2fa-verify-code" />
            <div className="mt-4 flex gap-2">
              <Button onClick={verify} disabled={submitting || code.length !== 6} className="h-11 bg-[#059669] text-white flex-1" data-testid="2fa-verify-btn"><ScanLine className="h-4 w-4 mr-2" /> Valider</Button>
              <Button onClick={() => { setSetup(null); setCode(""); }} variant="outline" className="h-11" data-testid="2fa-cancel">Annuler</Button>
            </div>
          </section>
        )}

        {!setup && (
          <section className="bg-[#FFFBEB] border border-[#FFD400]/40 p-6" data-testid="2fa-info">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D97706]">À savoir</div>
            <ul className="mt-3 space-y-2 text-xs leading-relaxed">
              <li>• Compatible avec Google Authenticator, Authy, 1Password, Bitwarden, Microsoft Authenticator</li>
              <li>• Le code change toutes les 30 secondes</li>
              <li>• En cas de perte de votre téléphone : contactez un autre admin pour réinitialiser depuis la base</li>
              <li>• Conservez le secret (Étape 1) dans votre gestionnaire de mots de passe</li>
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
